'use client';
import React, { useState } from 'react';
import { Flag, AlertTriangle, MessageSquare, Wifi, BookOpen, Loader2, CheckCircle } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


export type ReportContentType = 'contribution' | 'forum_post';

interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  contentType: ReportContentType;
  contentId: string;
  contentTitle?: string;
}

const REASONS: { value: string; label: string; description: string; icon: React.ElementType }[] = [
  {
    value: 'misinformation',
    label: 'Misinformation',
    description: 'Contains factually incorrect or misleading historical claims',
    icon: AlertTriangle,
  },
  {
    value: 'inappropriate',
    label: 'Inappropriate',
    description: 'Offensive, harmful, or violates community standards',
    icon: MessageSquare,
  },
  {
    value: 'spam',
    label: 'Spam',
    description: 'Irrelevant, repetitive, or promotional content',
    icon: Wifi,
  },
  {
    value: 'unsourced',
    label: 'Unsourced',
    description: 'Makes claims without any supporting sources or citations',
    icon: BookOpen,
  },
];

export default function ReportModal({ open, onClose, contentType, contentId, contentTitle }: ReportModalProps) {
  const { user } = useAuth();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (submitting) return;
    setSelectedReason('');
    setDetails('');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!user || !selectedReason) return;
    setSubmitting(true);
    setError(null);

    try {
      const supabase = createClient();
      const { error: insertError } = await supabase.from('content_reports').insert({
        reporter_id: user.id,
        content_type: contentType,
        content_id: contentId,
        reason: selectedReason,
        details: details.trim() || null,
        status: 'pending',
      });

      if (insertError) {
        if (insertError.code === '23505') {
          setError('You have already reported this content.');
        } else {
          setError('Failed to submit report. Please try again.');
        }
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contentLabel = contentType === 'contribution' ? 'contribution' : 'forum post';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Report ${contentLabel}`}
      maxWidth="max-w-md"
    >
      {submitted ? (
        <div className="flex flex-col items-center text-center py-4 gap-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#2C5E3B18' }}
          >
            <CheckCircle size={24} style={{ color: '#2C5E3B' }} />
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
              Report submitted
            </h4>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Thank you for helping keep the archive accurate. A curator will review this {contentLabel} shortly.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="mt-2 px-5 py-2 rounded-lg text-sm font-medium"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            Done
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Content reference */}
          {contentTitle && (
            <div
              className="px-3 py-2.5 rounded-lg border text-xs line-clamp-2"
              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
            >
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Reporting: </span>
              {contentTitle}
            </div>
          )}

          {/* Reason selection */}
          <div>
            <p className="text-xs font-semibold mb-2.5" style={{ color: 'var(--foreground)' }}>
              Select a reason <span style={{ color: 'var(--status-rejected)' }}>*</span>
            </p>
            <div className="space-y-2">
              {REASONS.map(({ value, label, description, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setSelectedReason(value)}
                  className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg border text-left transition-all duration-150"
                  style={{
                    backgroundColor: selectedReason === value ? 'var(--primary)08' : 'var(--card)',
                    borderColor: selectedReason === value ? 'var(--primary)' : 'var(--border)',
                  }}
                >
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{
                      backgroundColor: selectedReason === value ? 'var(--primary)18' : 'var(--muted)',
                      color: selectedReason === value ? 'var(--primary)' : 'var(--muted-foreground)',
                    }}
                  >
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-semibold"
                      style={{ color: selectedReason === value ? 'var(--primary)' : 'var(--foreground)' }}
                    >
                      {label}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      {description}
                    </p>
                  </div>
                  <div
                    className="w-4 h-4 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center"
                    style={{
                      borderColor: selectedReason === value ? 'var(--primary)' : 'var(--border)',
                      backgroundColor: selectedReason === value ? 'var(--primary)' : 'transparent',
                    }}
                  >
                    {selectedReason === value && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--primary-foreground)' }} />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Optional details */}
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--foreground)' }}>
              Additional details <span style={{ color: 'var(--muted-foreground)' }}>(optional)</span>
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide any additional context that would help curators review this report…"
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 rounded-lg border text-xs outline-none resize-none transition-colors"
              style={{
                backgroundColor: 'var(--input)',
                borderColor: 'var(--border)',
                color: 'var(--foreground)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
            <p className="text-xs mt-1 text-right" style={{ color: 'var(--muted-foreground)' }}>
              {details.length}/500
            </p>
          </div>

          {/* Error */}
          {error && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: '#E05A4712', color: 'var(--status-rejected)' }}>
              {error}
            </p>
          )}

          {/* Auth gate */}
          {!user && (
            <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
              <a href="/sign-up-login" style={{ color: 'var(--primary)' }}>Sign in</a> to submit a report.
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg text-sm transition-colors"
              style={{ color: 'var(--muted-foreground)' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedReason || !user || submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-all"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Flag size={14} />
                  Submit Report
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
